import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useTranslations } from 'gt-react'
import { AlertTriangle, Loader2, LogOut, Mail, Shield, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { WorkspacesSection } from '@/components/workspaces-section'
import { getCurrentUser, PlatformApiError, type PlatformUser, signOut } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Route search params. better-auth's verify-email endpoint redirects
 * here with `?error=CODE` when the verification token is expired or
 * invalid (see the `redirectOnError` path in better-auth's
 * email-verification.mjs). We parse it here so the component can
 * render a distinct error state without needing a separate route.
 */
export const Route = createFileRoute('/account/')({
  component: AccountPage,
  validateSearch: (search: Record<string, unknown>): { error?: string; sso?: string; checkout?: string } => {
    const error = typeof search.error === 'string' ? search.error : undefined
    const sso = typeof search.sso === 'string' ? search.sso : undefined
    const checkout = typeof search.checkout === 'string' ? search.checkout : undefined
    return { ...(error ? { error } : {}), ...(sso ? { sso } : {}), ...(checkout ? { checkout } : {}) }
  },
})

function AccountPage() {
  const t = useTranslations('account')
  const navigate = useNavigate()
  const { error: verifyError, sso: ssoTenantId } = Route.useSearch()

  // Auto-trigger SSO handoff when redirected from the ERP workspace
  // switcher with ?sso=<tenantId>. The platform session handles
  // authentication; the handoff creates a new tenant session and
  // redirects back to the ERP.
  useEffect(() => {
    if (!ssoTenantId) return
    import('@/lib/api').then(({ requestSsoHandoff }) =>
      requestSsoHandoff(ssoTenantId)
        .then(({ url }) => { window.location.href = url })
        .catch(() => {
          // SSO failed — stay on /account, strip the param
          void navigate({ to: '/account', search: {} })
        }),
    )
  }, [ssoTenantId, navigate])

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'authed'; user: PlatformUser }
    | { status: 'verify_failed'; code: string }
    | { status: 'error'; message: string }
  >(() =>
    // Initialize directly into verify_failed when we were
    // redirected here with ?error=… — skip the whoami call
    // entirely because the backend has NOT set a session
    // cookie and the /me fetch would just 401 into /sign-in,
    // losing the error context. Showing the failure here
    // with a direct "try signing in again" CTA is cleaner.
    verifyError ? { status: 'verify_failed', code: verifyError } : { status: 'loading' },
  )
  const [signingOut, setSigningOut] = useState(false)

  // Stash the latest navigate / t in refs so the mount effect can
  // call them without taking a dep on their identities — gt-react's
  // `t` is a NEW function reference on every render, so listing it
  // as a dep would re-fire the effect on every state update and
  // turn /me into a busy loop.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const fallbackErrorRef = useRef(t('error_load'))
  fallbackErrorRef.current = t('error_load')

  useEffect(() => {
    // Skip whoami entirely when the page was opened from a failed
    // verify-email redirect — no session exists, so /me would just
    // 401-bounce us to /sign-in and lose the error banner.
    if (verifyError) return

    const ctrl = new AbortController()
    getCurrentUser(ctrl.signal)
      .then((user) => setState({ status: 'authed', user }))
      .catch((err) => {
        // Aborted requests fire a DOMException with name='AbortError'.
        // That's the cleanup path running, not a real error — drop it.
        if (err instanceof DOMException && err.name === 'AbortError') return

        // 401 → not signed in → bounce to /sign-in.
        if (err instanceof PlatformApiError && err.status === 401) {
          void navigateRef.current({ to: '/sign-in' })
          return
        }

        // Anything else → show the actual server error message in dev
        // so the underlying problem is visible. The i18n fallback only
        // kicks in when we have no message at all.
        const message =
          err instanceof PlatformApiError && err.message ? err.message : fallbackErrorRef.current
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
    // Deps: only `verifyError` matters for re-running. The refs
    // above give us stable handles to navigate/t.
  }, [verifyError])

  async function handleSignOut() {
    setSigningOut(true)
    try {
      await signOut()
    } catch {
      // Even if the server call fails, we still want to bounce
      // them to /sign-in so the UI doesn't lie about being signed in.
    } finally {
      setSigningOut(false)
      void navigate({ to: '/sign-in' })
    }
  }

  return (
    <>
      <NavBar />
      <main className="flex min-h-screen flex-col">
        <section className="relative flex-1 pt-32 pb-24">
          <div
            className="pointer-events-none absolute inset-0 -z-10 marketing-grid-bg opacity-20"
            aria-hidden="true"
          />
          <div className="mx-auto w-full max-w-3xl px-4 sm:px-6">
            {state.status === 'loading' ? <LoadingState message={t('loading')} /> : null}
            {state.status === 'error' ? <ErrorState message={state.message} /> : null}
            {state.status === 'verify_failed' ? <VerifyFailedState code={state.code} /> : null}
            {state.status === 'authed' ? (
              <>
                <AuthedView
                  user={state.user}
                  t={t}
                  onSignOut={handleSignOut}
                  signingOut={signingOut}
                />
                <WorkspacesSection />
              </>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span>{message}</span>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-center text-sm text-destructive">
      {message}
    </div>
  )
}

/**
 * Shown when better-auth redirected here with `?error=CODE` after
 * a failed verify-email hop. No session exists — we skip the /me
 * call entirely and render a direct recovery CTA.
 *
 * Recovery path for the most common case (TOKEN_EXPIRED) is
 * "sign in and we'll auto-resend the verification email" — the
 * platform auth config has `sendOnSignIn: true`, so just trying
 * to sign in kicks off a fresh email.
 */
function VerifyFailedState({ code }: { code: string }) {
  const isExpired = code === 'TOKEN_EXPIRED'
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl border border-destructive/40 bg-destructive/5 p-8"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-destructive/10 text-destructive ring-1 ring-destructive/25">
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold text-destructive">
            {isExpired ? 'Your verification link expired' : 'This verification link is invalid'}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {isExpired
              ? "Verification links expire after 60 minutes for security. Sign in with your email and password and we'll send you a fresh one automatically."
              : "We couldn't verify this link. Sign in and we'll send you a fresh verification email."}
          </p>
          <div className="mt-4">
            <Link
              to="/sign-in"
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium',
                'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                'hover:opacity-90 transition-opacity',
              )}
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

function AuthedView({
  user,
  t,
  onSignOut,
  signingOut,
}: {
  user: PlatformUser
  t: (key: string) => string
  onSignOut: () => void
  signingOut: boolean
}) {
  const roleLabel =
    user.role === 'platform_admin'
      ? t('role_platform_admin')
      : user.role === 'platform_support'
        ? t('role_platform_support')
        : user.role === 'platform_readonly'
          ? t('role_platform_readonly')
          : user.role === 'customer_owner'
            ? t('role_customer_owner')
            : t('role_unknown')

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <header className="mb-8">
        <h1 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
          <span className="marketing-gradient-text">{t('heading')}</span>
        </h1>
        <p className="mt-2 text-muted-foreground">{t('subtitle')}</p>
      </header>

      <div className="rounded-2xl border border-border bg-card/80 backdrop-blur-md p-8 shadow-lg shadow-primary/5">
        <dl className="space-y-5">
          <Field icon={Mail} label={t('email_label')} value={user.email} />
          <Field icon={User} label={t('name_label')} value={user.name ?? t('name_unset')} />
          <Field icon={Shield} label={t('role_label')} value={roleLabel} />
        </dl>

        <div className="mt-8 flex justify-end border-t border-border pt-6">
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className={cn(
              'inline-flex items-center gap-2 rounded-full border border-border px-5 py-2 text-sm font-medium',
              'hover:bg-accent hover:text-accent-foreground transition-colors',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            <LogOut className="h-4 w-4" />
            {signingOut ? t('signing_out') : t('sign_out')}
          </button>
        </div>
      </div>
    </motion.div>
  )
}

function Field({ icon: Icon, label, value }: { icon: typeof Mail; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </dt>
        <dd className="mt-0.5 text-sm font-medium break-words">{value}</dd>
      </div>
    </div>
  )
}
